/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';

import Types = CommandTypes.Common.Applicationinfo;

const Applicationinfo = Commands.Common.Applicationinfo;

@Injectable({
    providedIn: 'root'
})
export class ApplicationInfoPlugin extends BrowserPlugin {
    private applicationLifeTimeSubject: Subject<any>;

    constructor() {
        super();
        this.applicationLifeTimeSubject = new Subject();
    }

    public get handlers(): PluginHandlers {
        return {
            module: Applicationinfo.IsApplicationRunning.module,
            commands: {
                [Applicationinfo.IsApplicationRunning.method]: this.isApplicationRunning.bind(this),
                [Applicationinfo.IsApplicationInstalled.method]: this.IsApplicationInstalled.bind(this),
                [Applicationinfo.GetIsUIRefreshed.method]: this.getIsUIRefreshed.bind(this),
                [Applicationinfo.RegisterAppLifetimeEventsCallback.method]:
                    this.registerAppLifetimeEventsCallback.bind(this),
                [Applicationinfo.OpenFileDialog.method]: this.openFileDialog.bind(this),
                [Applicationinfo.OpenDirectoryBrowser.method]: this.openDirectoryBrowser.bind(this)
            }
        };
    }

    private applicationLifeTimeListener(subject: Subject<any>) {
        window.addEventListener('unload', event => {
            // We only get unload event from browser which is handled below
            // Rest of the events like SystemSuspend are not available in browser
            subject.next({
                type: Types.ApplicationLifetimeEventType.ApplicationExit,
                reason: Types.ShutdownReason.BrowserExit
            });
        });
    }

    // Below command set to explicitly returns true on browser
    private isApplicationRunning(
        command: FullBridgeCommand<Types.IsApplicationRunningParameters>
    ): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    private IsApplicationInstalled(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    private getIsUIRefreshed(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        // explicit false to ignore refresh scenarion in case of browser
        return this.makeResult('false');
    }

    private registerAppLifetimeEventsCallback(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        this.applicationLifeTimeListener(this.applicationLifeTimeSubject);
        return this.makePersistentResult(this.applicationLifeTimeSubject.asObservable());
    }

    // Below command is not used in case of browser
    private openFileDialog(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makeResult('true');
    }

    // Below command is not used in case of browser
    private openDirectoryBrowser(
        command: FullBridgeCommand<Types.OpenDirectoryBrowserParameters>
    ): Observable<PluginResponse> {
        return this.makeResult('true');
    }
}
